import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ApiClient } from '../../../api/apiClient';
import { IonSpinner } from '@ionic/react';

export enum SubscriptionStatus {
    ACTIVE = 'active',
    PAST_DUE = 'past_due',
    CANCELED = 'canceled',
    UNPAID = 'unpaid',
    INCOMPLETE = 'incomplete',
    INCOMPLETE_EXPIRED = 'incomplete_expired',
    TRIALING = 'trialing',
    PAUSED = 'paused'
}

interface SubscriptionState {
    hasSubscription: boolean;
    subscriptionLevel: string;
    subscriptionStatus: string;
    accountId: string;
    isReady: boolean;
    error?: string;
}

interface SubscriptionContextType {
    subscriptionState: SubscriptionState;
    refreshSubscriptionState: () => Promise<void>;
    isSubscriptionValid: boolean;
    isSubscriptionBlocked: boolean;
    canAccessFeature: (feature: SubscriptionFeature) => boolean;
}

export enum SubscriptionFeature {
    BASIC_LINKS = 'basic_links',
    ADVANCED_LINKS = 'advanced_links',
    CUSTOM_BRANDING = 'custom_branding',
    TEAM_MEMBERS = 'team_members',
    API_ACCESS = 'api_access',
    CUSTOM_DOMAIN = 'custom_domain',
    ANALYTICS = 'analytics',
    ENHANCED_ANALYTICS = 'enhanced_analytics',
    ADVANCED_ANALYTICS = 'advanced_analytics'
}

const featureMatrix: Record<string, SubscriptionFeature[]> = {
    basic: [
        SubscriptionFeature.BASIC_LINKS,
        SubscriptionFeature.ANALYTICS
    ],
    pro: [
        SubscriptionFeature.BASIC_LINKS,
        SubscriptionFeature.ADVANCED_LINKS,
        SubscriptionFeature.CUSTOM_BRANDING,
        SubscriptionFeature.TEAM_MEMBERS,
        SubscriptionFeature.ANALYTICS,
        SubscriptionFeature.ENHANCED_ANALYTICS
    ],
    business: [
        SubscriptionFeature.BASIC_LINKS,
        SubscriptionFeature.ADVANCED_LINKS,
        SubscriptionFeature.CUSTOM_BRANDING,
        SubscriptionFeature.TEAM_MEMBERS,
        SubscriptionFeature.API_ACCESS,
        SubscriptionFeature.CUSTOM_DOMAIN,
        SubscriptionFeature.ANALYTICS,
        SubscriptionFeature.ENHANCED_ANALYTICS,
        SubscriptionFeature.ADVANCED_ANALYTICS
    ]
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [subscriptionState, setSubscriptionState] = useState<SubscriptionState>({
        hasSubscription: false,
        subscriptionLevel: '',
        subscriptionStatus: '',
        accountId: '',
        isReady: false,
        error: undefined
    });

    const { currentUser, authState, isLoading: isAuthLoading } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const lockApiCalls = useRef(false);

    const refreshSubscriptionState = async () => {
        if (!currentUser) {
            setSubscriptionState(prev => ({ ...prev, isReady: true, error: undefined }));
            return;
        }

        if (lockApiCalls.current) return;
        lockApiCalls.current = true;

        try {
            setSubscriptionState(prev => ({ ...prev, isReady: false, error: undefined }));

            const apiClient = ApiClient.getInstance();
            const response = await apiClient.get('/accounts/current');
            const account = response.data;
            const hasValidSubscription = account?.subscriptionStatus === SubscriptionStatus.ACTIVE ||
                account?.subscriptionStatus === SubscriptionStatus.TRIALING;

            setSubscriptionState({
                hasSubscription: hasValidSubscription,
                subscriptionLevel: account?.subscriptionLevel || '',
                subscriptionStatus: account?.subscriptionStatus || '',
                accountId: account?.id || '',
                isReady: true,
                error: undefined
            });
        } catch (error) {
            console.error('Error refreshing subscription state:', error);
            setSubscriptionState(prev => ({
                ...prev,
                isReady: true,
                error: 'Failed to load subscription details. Please try again.'
            }));
        } finally {
            lockApiCalls.current = false;
        }
    };

    // Compute derived subscription states
    const isSubscriptionValid = subscriptionState.hasSubscription && 
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(subscriptionState.subscriptionStatus as SubscriptionStatus);

    const isSubscriptionBlocked = subscriptionState.hasSubscription && 
        [SubscriptionStatus.PAST_DUE, SubscriptionStatus.UNPAID].includes(subscriptionState.subscriptionStatus as SubscriptionStatus);

    const canAccessFeature = (feature: SubscriptionFeature): boolean => {
        if (!isSubscriptionValid) return false;
        return featureMatrix[subscriptionState.subscriptionLevel]?.includes(feature) ?? false;
    };

    useEffect(() => {
        if (authState === 'AUTHENTICATED') {
            refreshSubscriptionState();
        }
    }, [authState, currentUser]);

    useEffect(() => {
        // Only handle routing when auth is done and subscription data is loaded
        if (!isAuthLoading && subscriptionState.isReady && authState === 'AUTHENTICATED') {
            const isSubscriptionPage = location.pathname === '/subscription';
            const isAuthPage = ['/login', '/register', '/verify-magic-link'].includes(location.pathname);
            const isVerifySubscriptionPage = location.pathname === '/verify-subscription';
            const isAccountPage = location.pathname === '/account';

            if (isSubscriptionBlocked && !isAccountPage) {
                history.replace('/account');
            } else if (!isSubscriptionValid && !isSubscriptionPage && !isAuthPage && !isVerifySubscriptionPage) {
                history.replace('/subscription');
            } else if (isSubscriptionValid && isSubscriptionPage) {
                history.replace('/');
            }
        }
    }, [subscriptionState, location.pathname, authState, isAuthLoading]);

    // Update the loading state to show error if present
    if (authState === 'AUTHENTICATED' && (!subscriptionState.isReady || subscriptionState.error)) {
        return (
            <div className="ion-text-center ion-padding" style={{ marginTop: '40vh' }}>
                {!subscriptionState.error ? (
                    <>
                        <IonSpinner name="crescent" />
                        <p>Loading subscription details...</p>
                    </>
                ) : (
                    <>
                        <p style={{ color: 'var(--ion-color-danger)' }}>{subscriptionState.error}</p>
                        <button 
                            onClick={refreshSubscriptionState}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                backgroundColor: 'var(--ion-color-primary)',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            Retry
                        </button>
                    </>
                )}
            </div>
        );
    }

    return (
        <SubscriptionContext.Provider 
            value={{ 
                subscriptionState, 
                refreshSubscriptionState,
                isSubscriptionValid,
                isSubscriptionBlocked,
                canAccessFeature
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
}; 